export const BACKEND_URI = ''

import { uploadDirect } from '@lorefully/upload'
import type { ResumableUploadOffer } from '@lorefully/upload'

import {
    ChatAppRequest,
    SimpleAPIResponse,
    FeedbackRequest,
    PollResponse,
    PollsResponse,
    CreatePollRequest,
    VoteRequest,
    VoteResponse,
    QuestionsResponse,
    QuestionResponse,
    UpvoteResponse,
    AnonymousSessionResponse,
    RegistrationResponse,
    LoginResponse,
    EmailVerificationResponse,
    EmailAvailabilityResponse,
    InviteDetailsResponse,
    JoinCodePreviewResponse,
    RedeemCodeResponse,
    AnonymousRedeemResponse,
    PendingInvitesResponse,
    SessionResponse,
} from './models'
import type { RAGSettings } from './models'
import type { QuestionWordCloud } from '../types/wordcloud'

/**
 * Get HTTP headers for API requests.
 *
 * Auth and tenant context are cookie-based — cookies are sent automatically
 * by the browser. The backend resolves the tenant from the JWT `tenant_id`
 * claim (authenticated) or AnonymousSession.tenant_id (anonymous).
 *
 * @param _idToken Ignored. Kept for compatibility with legacy call sites
 *                 that still thread a token through.
 */
export async function getHeaders(
    _idToken?: string
): Promise<Record<string, string>> {
    return {}
}

/**
 * Flag to prevent multiple simultaneous refresh attempts.
 * When one request triggers refresh, others wait for it to complete.
 */
let isRefreshing = false
let refreshPromise: Promise<RefreshOutcome> | null = null

/**
 * How a refresh ended.
 *
 * - `refreshed`: new tokens are in the cookies.
 * - `rejected`: the server said the session is over (401/403).
 * - `unavailable`: the server could not decide. That covers a 503 because Redis
 *   could not be asked whether the token was revoked, any other 5xx, a 429, or
 *   no answer at all. The same session works again once the server can answer.
 *   On 2026-10-20 this was read as `rejected`, and the hour Redis was down
 *   signed out everyone whose access token came up for renewal.
 */
export type RefreshOutcome = 'refreshed' | 'rejected' | 'unavailable'

/**
 * Refresh the access token using the refresh token in httpOnly cookie.
 * Returns true if refresh succeeded, false otherwise.
 */
export async function refreshAccessToken(): Promise<boolean> {
    return (await refreshAccessTokenOutcome()) === 'refreshed'
}

/** {@link refreshAccessToken}, saying why it failed. */
export async function refreshAccessTokenOutcome(): Promise<RefreshOutcome> {
    // If already refreshing, wait for that promise
    if (isRefreshing && refreshPromise) {
        return refreshPromise
    }

    isRefreshing = true
    refreshPromise = (async () => {
        try {
            // Use plain fetch to avoid infinite loop
            const response = await fetch(`${BACKEND_URI}/api/v1/auth/refresh`, {
                method: 'POST',
                credentials: 'include', // Send httpOnly cookies
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (response.ok) {
                console.log('Token refreshed successfully')

                // Dispatch event to notify AuthContext to revalidate
                window.dispatchEvent(new CustomEvent('auth:token-refreshed'))

                return 'refreshed'
            }

            console.error('Token refresh failed:', response.status)
            return response.status === 401 || response.status === 403
                ? 'rejected'
                : 'unavailable'
        } catch (error) {
            console.error('Token refresh error:', error)
            return 'unavailable'
        } finally {
            isRefreshing = false
            refreshPromise = null
        }
    })()

    return refreshPromise
}

/**
 * Enhanced fetch wrapper that automatically refreshes token on 401.
 * If token refresh succeeds, retries the original request.
 *
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @returns Response object
 */
export async function fetchWithAuth(
    url: string,
    options: RequestInit = {}
): Promise<Response> {
    // First attempt - use plain fetch
    let response = await fetch(url, {
        ...options,
        credentials: 'include', // Always send cookies
    })

    // If 401, try to refresh and retry.
    // Public auth endpoints (login, register, verify-email, etc.) use plain
    // fetch directly and never reach here, so any 401 here is a genuinely
    // expired session on an authenticated endpoint.
    if (response.status === 401) {
        const outcome = await refreshAccessTokenOutcome()

        if (outcome === 'refreshed') {
            // Retry original request with new token
            response = await fetch(url, {
                ...options,
                credentials: 'include',
            })
        } else if (outcome === 'rejected') {
            // Only dispatch for non-auth-utility calls to avoid the
            // auth:refresh-failed → signOut → logout → auth:refresh-failed loop.
            // Never for `unavailable`: its listener calls logout, which would end
            // a session the server never said was over.
            if (
                !url.includes('/auth/logout') &&
                !url.includes('/auth/refresh')
            ) {
                window.dispatchEvent(new CustomEvent('auth:refresh-failed'))
            }
        }
    }

    return response
}

/**
 * Pull a human-readable message out of an error response body.
 *
 * The backend puts it in `detail`, always: every exception handler in
 * `api/main.py:689-765` returns `{"detail": ...}` and none of them returns
 * `{"error": ...}`. Reading only `.error` therefore matched nothing, so every
 * backend failure — a 429 from the rate limiter, a 400 on a missing tenant, a
 * 403 from the anonymous-access gate — arrived as the string "Unknown error"
 * with the real reason discarded before any caller could show it.
 *
 * `.error` is still read first because a handful of auth routes in this file
 * already relied on it; this widens the extraction rather than swapping it.
 *
 * FastAPI's `detail` is a string for a raised HTTPException but a list of
 * `{loc, msg, type}` objects for a 422 validation failure. Normalising that
 * here keeps `[object Object]` out of the UI without every call site knowing.
 */
export function extractApiError(body: unknown, fallback: string): string {
    if (!body || typeof body !== 'object') return fallback

    const { error, detail } = body as { error?: unknown; detail?: unknown }

    if (typeof error === 'string' && error) return error
    if (typeof detail === 'string' && detail) return detail

    if (Array.isArray(detail)) {
        const messages = detail
            .map((item) =>
                item && typeof item === 'object' && 'msg' in item
                    ? String((item as { msg: unknown }).msg)
                    : ''
            )
            .filter(Boolean)
        if (messages.length > 0) return messages.join('. ')
    }

    return fallback
}

interface ContributeRequest {
    data: string
    conversationId?: string // Optional conversation ID for ongoing conversations
    questionId?: string
    inputSource?: 'text' | 'voice' | 'mixed'
    /** The recording these words were captured from, when they were dictated. */
    recordingId?: string
    /**
     * Whether the words may still be improved on. `preview` is provisional — a
     * transcript of the same audio may replace them. `human` means the contributor
     * edited them, which settles them permanently.
     */
    contentSource?: 'preview' | 'transcript' | 'human'
}

export interface ContributeResponse {
    message: string
    conversationId: string // The conversation ID for this interaction
    isNewConversation: boolean // Whether this started a new conversation
    messageCount: number // Total number of messages in the conversation
}

function decodeHtmlEntities(text: string): string {
    if (!text) return ''

    // Using the browser's built-in HTML parsing
    const textArea = document.createElement('textarea')
    textArea.innerHTML = text
    return textArea.value
}

export async function answerApi(
    request: ContributeRequest,
    idToken: string | undefined
): Promise<ContributeResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/contributions`,
        {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(request),
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(
            extractApiError(
                parsedResponse,
                "We couldn't save your contribution. Please try again."
            )
        )
    }

    // Decode HTML entities in the message before returning
    if (parsedResponse.message) {
        parsedResponse.message = decodeHtmlEntities(parsedResponse.message)
    }

    return parsedResponse as ContributeResponse
}

export async function closeConversationApi(
    conversationId: string,
    idToken: string | undefined
): Promise<{ message: string }> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/conversations/${conversationId}/close`,
        {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({}),
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(
            extractApiError(
                parsedResponse,
                "We couldn't close that conversation."
            )
        )
    }

    return parsedResponse as { message: string }
}

export async function feedbackApi(
    request: FeedbackRequest,
    idToken: string | undefined
): Promise<{ message: string }> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(`${BACKEND_URI}/api/v1/feedback`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
    })

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse as { message: string }
}

export async function ragFeedbackApi(
    messageId: string,
    request: {
        rating: number
        feedbackText?: string | null
        feedbackType?: string
    },
    idToken: string | undefined
): Promise<{
    id: string
    messageId: string
    rating: number
    feedbackType: string
    createdAt: string
}> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/rag/messages/${messageId}/feedback`,
        {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(request),
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export const DEFAULT_RAG_SETTINGS: RAGSettings = {
    top: 8,
    maxTokens: 2048,
    optimiseQuery: true,
    generateFollowups: false,
    // Dense only: it beat hybrid on the 2026-09-29 graded set (ADR-0037, 2026-10-19).
    useHybridSearch: false,
    useQueryDecomposition: true,
    useMultiStep: true,
}

export async function chatApi(
    request: ChatAppRequest,
    shouldStream: boolean,
    idToken: string | undefined
): Promise<Response> {
    let url = `${BACKEND_URI}/api/v1/rag/chat`
    if (shouldStream) {
        url += '/stream'
    }
    const headers = await getHeaders(idToken)
    const lastMessage = request.messages[request.messages.length - 1]
    const body = {
        query: lastMessage.content,
        conversation_id: request.session_state ?? null,
        settings: request.context?.settings ?? DEFAULT_RAG_SETTINGS,
    }
    return await fetchWithAuth(url, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
    })
}

export async function fastTranscriptionApi(
    formData: FormData
): Promise<{ text: string }> {
    const headers = await getHeaders(undefined)
    // The canonical path. `/speech/fastapi` is a legacy alias of the same route,
    // kept server-side only until cached clients have moved on.
    const response = await fetchWithAuth('/api/v1/speech/transcribe', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
    })
    if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error:', errorText)
        throw new Error(
            `Failed to fetch speech data: ${response.status} - ${errorText}`
        )
    }
    return await response.json()
}

// ── Interview transcription (async: create → upload → commit → poll) ────────
// Long interview voice contributions are transcribed server-side (chunked-inline
// chirp_3) and LLM role-labelled, so the flow is asynchronous with status polling.
//
// These four now speak to `/recordings` (ADR-0016) rather than `/speech/interview/*`.
// The shapes below are kept deliberately: a recording is reserved with `kind:
// 'interview'` and the contributor's own scope, so the endpoints differ but the flow
// does not, and `micprototype.tsx` — whose recording state machine belongs to the
// design workstream — needs no change beyond dropping an argument that no longer
// exists.

export interface InterviewJob {
    task_id: string
    upload_url: string
    /**
     * Present when the server offers a resumable session. **Absent means single-shot** —
     * PUT the whole blob to `upload_url`, exactly as before — which is what lets the
     * backend turn resumability on per environment without a frontend release.
     */
    upload?: ResumableUploadOffer | null
}

// `storage_path` is gone, not renamed. The server derives the path at reserve and signs
// the upload URL for exactly that object; accepting one back is what let a caller name
// any object in the container as the recording to transcribe, serve and delete.

export interface InterviewStatus {
    task_id: string
    status: 'processing' | 'completed' | 'failed'
    stage: string | null
    transcript: string | null
    error: string | null
}

export async function createInterviewApi(body: {
    filename: string
    title?: string
    language?: string
    file_size_bytes?: number
    /** What this capture will attempt. See the backend's `CaptureIntent`. */
    capture_intent?: 'preview_only' | 'preview_with_fallback' | 'audio_required'
    /**
     * Makes the reservation replayable. Without it a retried reserve — a dropped
     * response, a tab restored — mints a *second* row and orphans the first at
     * `pending_upload` until the reaper fails it.
     */
    idempotency_key?: string
}): Promise<InterviewJob> {
    const headers = await getHeaders(undefined)
    const response = await fetchWithAuth(`${BACKEND_URI}/api/v1/recordings`, {
        method: 'POST',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
            ...(body.idempotency_key
                ? { 'Idempotency-Key': body.idempotency_key }
                : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
            filename: body.filename,
            title: body.title,
            language: body.language,
            fileSizeBytes: body.file_size_bytes,
            captureIntent: body.capture_intent,
            // The kind is checked against the caller's *scope*, not their role: a
            // contributor may reserve an interview and cannot reserve corpus audio, and
            // an admin the reverse. Asking for the wrong one is a 403, not a silent
            // reclassification.
            kind: 'interview',
        }),
    })
    if (!response.ok) {
        throw new Error(`Failed to create interview job: ${response.status}`)
    }
    const job = await response.json()
    return { task_id: job.id, upload_url: job.uploadUrl, upload: job.upload }
}

export async function uploadInterviewAudioApi(
    uploadUrl: string,
    blob: Blob,
    offer?: InterviewJob['upload']
): Promise<void> {
    // `uploadDirect` is the shared transport (`packages/upload`), not a second copy of
    // this logic. There were two: this one and the admin console's, and they had already
    // drifted — only one sent Azure's required `x-ms-blob-type`, and only one knew that
    // a same-origin path needs the auth cookie while a signed URL must never receive it.
    //
    // When the reserve step offered a resumable session it sends the recording in
    // chunks, so a dropped connection costs one chunk rather than the whole recording: a
    // 25-minute contribution that fails at 90% used to be gone, not retried.
    return uploadDirect(uploadUrl, blob, {
        offer,
        contentType: blob.type || 'audio/webm',
    })
}

export async function finaliseInterviewApi(taskId: string): Promise<void> {
    const headers = await getHeaders(undefined)
    // No body. The server already knows where the bytes are — it chose the path and
    // signed the URL for it, so there is nothing for the client to assert.
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/recordings/${taskId}/commit`,
        {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        }
    )
    if (!response.ok) {
        throw new Error(`Failed to finalise interview: ${response.status}`)
    }
}

/** How far an upload got before it failed. Mirrors the backend's `UPLOAD_FAILURE_STAGES`. */
export type UploadFailureStage = 'reserve' | 'transfer' | 'commit'

/** Best-effort classification of what went wrong. Unknown values are clamped server-side. */
export type UploadFailureReason =
    | 'network'
    | 'http_error'
    | 'empty_recording'
    | 'session_expired'
    | 'aborted'
    | 'other'

/**
 * Tell the server an upload did not make it.
 *
 * The server cannot find this out on its own: the bytes go from this browser straight
 * to the storage provider, so a failed transfer is invisible to it, and a failed
 * *reservation* leaves no row to notice later either. Without this the only trace of a
 * lost recording was a `console.error` on this device.
 *
 * **Never throws, and is never awaited by the caller.** It runs on a connection that
 * has just failed, on the teardown path of a recording the contributor has already
 * finished with — a rejection here would replace one lost recording with an unhandled
 * rejection, and a delay here would be a delay they can see.
 */
export function reportUploadFailureApi(report: {
    stage: UploadFailureStage
    reason: UploadFailureReason
    recording_id?: string | null
}): void {
    void (async () => {
        try {
            const headers = await getHeaders(undefined)
            await fetchWithAuth(
                `${BACKEND_URI}/api/v1/recordings/upload-failed`,
                {
                    method: 'POST',
                    headers: { ...headers, 'Content-Type': 'application/json' },
                    credentials: 'include',
                    // Survives the page being closed on the way out, which is one of the
                    // ways a contribution is lost in the first place.
                    keepalive: true,
                    body: JSON.stringify({
                        stage: report.stage,
                        reason: report.reason,
                        recordingId: report.recording_id ?? null,
                    }),
                }
            )
        } catch {
            // Reporting that reporting failed has nowhere to go.
        }
    })()
}

/**
 * What kind of failure this was, from whatever the upload threw.
 *
 * Deliberately coarse. The value becomes a bounded metric label, and the server clamps
 * anything it does not recognise, so guessing wrong costs a little precision and never
 * the report itself.
 */
export function classifyUploadFailure(error: unknown): UploadFailureReason {
    const message = error instanceof Error ? error.message : String(error ?? '')
    if (/empty recording/i.test(message)) return 'empty_recording'
    if (/expired/i.test(message)) return 'session_expired'
    if (/abort/i.test(message)) return 'aborted'
    if (/network error/i.test(message)) return 'network'
    if (/status \d{3}|failed with status|: \d{3}$/i.test(message))
        return 'http_error'
    return 'other'
}

export async function getInterviewStatusApi(
    taskId: string
): Promise<InterviewStatus> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/recordings/${taskId}`,
        { method: 'GET', credentials: 'include' }
    )
    if (!response.ok) {
        throw new Error(`Failed to get interview status: ${response.status}`)
    }
    const detail = await response.json()
    // `interviewStatus`, not `status`. An interview is a *completed transcription job*
    // while it is still being role-labelled, so branching on the job status would tell a
    // contributor their contribution was ready and hand them an unlabelled transcript of
    // their own voice. The server derives the contributor-facing view; this does not
    // re-derive it.
    return {
        task_id: detail.id,
        status: detail.interviewStatus,
        stage: detail.interviewStage ?? null,
        transcript: detail.interviewTranscript ?? null,
        error: detail.error ?? null,
    }
}

// ── Session capture (facilitator live-recording workflow) ───────────────────
// A capture is a ContentSession plus a live Recording, created and opened in
// one call so a facilitator standing at a table can start immediately with
// nothing to point at yet. See `backend/src/api/routes/sessions/`.

export interface SessionCapture {
    recordingId: string
    wsPath: string
    teeEnabled: boolean
    uploadEnabled: boolean
    audio: { sampleRate: number; encoding: string; frameMs: number }
    contentSessionId: string
    /** The capture this part belongs to; `contentSessionId` is the part. */
    sessionId: string
    name: string
    slug: string
}

export async function startSessionCaptureApi(body: {
    name?: string
    description?: string
}): Promise<SessionCapture> {
    const headers = await getHeaders(undefined)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/sessions/capture`,
        {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
        }
    )
    if (!response.ok) {
        const errorBody = await response.json().catch(() => null)
        throw new Error(
            extractApiError(
                errorBody,
                `Failed to start session: ${response.status}`
            )
        )
    }
    return await response.json()
}

export interface CloseCaptureResult {
    recordingId: string
    status: string
}

/** Attempts at closing a capture, and the wait before the second (doubled after). */
export const CLOSE_CAPTURE_ATTEMPTS = 3
export const CLOSE_CAPTURE_RETRY_MS = 1000

/** A status worth another try: the server could not answer, rather than refused. */
const isRetryableStatus = (status: number): boolean =>
    status >= 500 || status === 408 || status === 429

/**
 * End a capture. Retried on a network error or a 5xx/408/429.
 *
 * The server answers a replayed close with the recording's current state rather
 * than a conflict, so retrying is safe — and it is worth it: a close that fails
 * leaves the capture `live` until the four-hour cap, with its summary and
 * transcript waiting on it. A 503 while Redis fails over is exactly that case.
 */
export async function closeSessionCaptureApi(
    contentSessionId: string,
    body: { completeSession?: boolean } = {}
): Promise<CloseCaptureResult> {
    const headers = await getHeaders(undefined)
    let lastError: unknown = null
    for (let attempt = 1; attempt <= CLOSE_CAPTURE_ATTEMPTS; attempt++) {
        if (attempt > 1) {
            await new Promise((resolve) =>
                setTimeout(resolve, CLOSE_CAPTURE_RETRY_MS * 2 ** (attempt - 2))
            )
        }
        let response: Response
        try {
            response = await fetchWithAuth(
                `${BACKEND_URI}/api/v1/sessions/${contentSessionId}/capture/close`,
                {
                    method: 'POST',
                    headers: { ...headers, 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(body),
                }
            )
        } catch (err) {
            lastError = err
            continue
        }
        if (response.ok) return await response.json()
        const errorBody = await response.json().catch(() => null)
        lastError = new Error(
            extractApiError(
                errorBody,
                `Failed to close session: ${response.status}`
            )
        )
        if (!isRetryableStatus(response.status)) break
    }
    throw lastError
}

export interface CaptureSummaryPayload {
    themes: { name: string; summary: string }[]
    keyPoints: { point: string; stance: string }[]
    suggestedQuestions: string[]
    decisions: string[]
    nextSteps: string[]
    overview: string
    title: string
    lastSeq: number
}

export interface CaptureSummaryResponse {
    recordingId: string
    contentSessionId: string
    status: string
    stage: string | null
    mode: string | null
    isFinal: boolean
    summary: CaptureSummaryPayload | null
    summaryAt: string | null
    transcriptReady: boolean
}

export async function getSessionCaptureSummaryApi(
    contentSessionId: string
): Promise<CaptureSummaryResponse> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/sessions/${contentSessionId}/capture/summary`,
        { method: 'GET', credentials: 'include' }
    )
    if (!response.ok) {
        throw new Error(`Failed to get capture summary: ${response.status}`)
    }
    return await response.json()
}

/** A capture's own summary, across all its parts — see `GET /sessions/{id}/summary`. */
export interface SessionRollupResponse {
    sessionId: string
    sessionName: string
    partCount: number
    /** How many parts this summary was written over; behind `partCount` while
     * the roll-up catches up with a part just recorded. */
    coversParts: number
    /** Time recorded across every part. */
    durationSeconds: number
    stage: string | null
    mode: string | null
    isFinal: boolean
    summary: CaptureSummaryPayload | null
    summaryAt: string | null
}

export async function getSessionRollupApi(
    sessionId: string
): Promise<SessionRollupResponse> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/sessions/${sessionId}/summary`,
        { method: 'GET', credentials: 'include' }
    )
    if (!response.ok) {
        throw new Error(`Failed to get session summary: ${response.status}`)
    }
    return await response.json()
}

// One card in the Sessions Log — see `GET /sessions/summaries`.
export interface SessionSummaryListItem {
    id: string
    name: string
    at: string | null
    overview: string
    /** No summary has resolved yet — it is still being processed. */
    summaryPending: boolean
    /** No summary will ever come: no audio, a failed recording, or too little to summarise. */
    summaryUnavailable: boolean
    partCount: number
    /** The group a table belongs to; null for a single capture. */
    groupName: string | null
    /** When recording first started. */
    startedAt: string | null
    /** Time recorded across every part. */
    durationSeconds: number
}

export interface ListSessionSummariesResponse {
    items: SessionSummaryListItem[]
    totalCount: number
    limit: number
    offset: number
    hasMore: boolean
}

export async function listSessionSummariesApi(
    params: { limit?: number; offset?: number } = {}
): Promise<ListSessionSummariesResponse> {
    const query = new URLSearchParams()
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.offset !== undefined) query.set('offset', String(params.offset))
    const qs = query.toString()
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/sessions/summaries${qs ? `?${qs}` : ''}`,
        { method: 'GET', credentials: 'include' }
    )
    if (!response.ok) {
        throw new Error(`Failed to list capture summaries: ${response.status}`)
    }
    return await response.json()
}

export type ParsedCitation = {
    /** Filepath portion (after `|` if present), with `#hash` retained. */
    path: string
    /** Filepath portion without trailing `#fragment`. */
    pathWithoutHash: string
    /** Lower-case file extension of `pathWithoutHash`, or `''`. */
    extension: string
    /** Hash fragment without the leading `#`, or `''`. */
    hash: string
}

export function parseCitation(citation: string): ParsedCitation {
    const parts = citation.split('|')
    const path = parts.length > 1 ? parts[1] : citation
    const hashIndex = path.indexOf('#')
    const pathWithoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path
    const hash = hashIndex >= 0 ? path.slice(hashIndex + 1) : ''
    const dotIndex = pathWithoutHash.lastIndexOf('.')
    const extension =
        dotIndex >= 0 ? pathWithoutHash.slice(dotIndex + 1).toLowerCase() : ''
    return { path, pathWithoutHash, extension, hash }
}

export function getCitationFilePath(citation: string): string {
    const { path, pathWithoutHash, hash } = parseCitation(citation)
    if (
        path.startsWith('/api/') ||
        path.startsWith('blob:') ||
        /^https?:\/\//i.test(path)
    ) {
        return path
    }

    const encodedPath = pathWithoutHash
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/')
    const hashSuffix = hash ? `#${hash}` : ''
    return `${BACKEND_URI}/content/${encodedPath}${hashSuffix}`
}

// Poll API functions
export async function getPollApi(
    pollId: string,
    idToken: string | undefined
): Promise<PollResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/poll/${pollId}`,
        {
            method: 'GET',
            headers: { ...headers },
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function createPollApi(
    request: CreatePollRequest,
    idToken: string | undefined
): Promise<PollResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/tenant-admin/polls`,
        {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(request),
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function pausePollApi(
    pollId: string,
    idToken: string | undefined
): Promise<void> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/tenant-admin/polls/${pollId}/pause`,
        {
            method: 'POST',
            headers,
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }
}

export async function resumePollApi(
    pollId: string,
    idToken: string | undefined
): Promise<void> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/tenant-admin/polls/${pollId}/resume`,
        {
            method: 'POST',
            headers,
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }
}

export async function getPollsApi(
    idToken: string | undefined,
    limit?: number
): Promise<PollsResponse> {
    const headers = await getHeaders(idToken)
    const url = limit
        ? `${BACKEND_URI}/api/v1/polls?limit=${limit}`
        : `${BACKEND_URI}/api/v1/polls`
    const response = await fetchWithAuth(url, {
        method: 'GET',
        headers: { ...headers },
        credentials: 'include',
    })

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function getAdminPollsApi(
    idToken: string | undefined,
    limit?: number
): Promise<PollsResponse> {
    const headers = await getHeaders(idToken)
    const url = limit
        ? `${BACKEND_URI}/api/v1/tenant-admin/polls?limit=${limit}`
        : `${BACKEND_URI}/api/v1/tenant-admin/polls`

    const response = await fetchWithAuth(url, {
        method: 'GET',
        headers: { ...headers },
        credentials: 'include',
    })

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function votePollApi(
    request: VoteRequest,
    idToken: string | undefined
): Promise<VoteResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(`${BACKEND_URI}/api/v1/poll/vote`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
    })

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function getQuestionsApi(
    idToken: string | undefined,
    limit?: number
): Promise<QuestionsResponse> {
    const headers = await getHeaders(idToken)
    const url = limit
        ? `${BACKEND_URI}/api/v1/questions?limit=${limit}`
        : `${BACKEND_URI}/api/v1/questions`
    const response = await fetchWithAuth(url, {
        method: 'GET',
        headers: { ...headers },
        credentials: 'include',
    })

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function getQuestionApi(
    questionId: string,
    idToken: string | undefined
): Promise<QuestionResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/question?id=${questionId}`,
        {
            method: 'GET',
            headers: { ...headers },
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function createQuestionApi(
    request: { question: string },
    idToken: string | undefined
): Promise<QuestionResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(`${BACKEND_URI}/api/v1/questions`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
    })

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function upvoteQuestionApi(
    request: { questionId: string },
    idToken: string | undefined
): Promise<UpvoteResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/questions/${request.questionId}/upvote`,
        {
            method: 'POST',
            headers,
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function removeUpvoteQuestionApi(
    request: { questionId: string },
    idToken: string | undefined
): Promise<UpvoteResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/questions/${request.questionId}/upvote`,
        {
            method: 'DELETE',
            headers,
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function markQuestionAnsweredApi(
    request: { questionId: string },
    idToken: string | undefined
): Promise<QuestionResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/questions/${request.questionId}/mark-answered`,
        {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function getWordCloudApi(
    questionId: string,
    idToken: string | undefined
): Promise<QuestionWordCloud> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/wordcloud/${questionId}`,
        {
            method: 'GET',
            headers: { ...headers },
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function uploadFileApi(
    request: FormData,
    idToken: string
): Promise<SimpleAPIResponse> {
    const response = await fetchWithAuth('/api/v1/upload', {
        method: 'POST',
        headers: await getHeaders(idToken),
        credentials: 'include',
        body: request,
    })

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`)
    }

    const dataResponse: SimpleAPIResponse = await response.json()
    return dataResponse
}

export async function deleteUploadedFileApi(
    filename: string,
    idToken: string
): Promise<SimpleAPIResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth('/api/v1/delete_uploaded', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ filename }),
    })

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`)
    }

    const dataResponse: SimpleAPIResponse = await response.json()
    return dataResponse
}

export async function listUploadedFilesApi(idToken: string): Promise<string[]> {
    const response = await fetchWithAuth(`/api/v1/list_uploaded`, {
        method: 'GET',
        headers: await getHeaders(idToken),
        credentials: 'include',
    })

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`)
    }

    const dataResponse: string[] = await response.json()
    return dataResponse
}

export interface FileInfo {
    name: string
    lastModifiedDate: string | null
}

export async function listAllUploadedFilesApi(
    idToken: string | undefined
): Promise<FileInfo[]> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/list_uploaded', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            if (response.status === 403) {
                throw new Error(
                    'Admin access required to list all uploaded files'
                )
            }
            throw new Error(
                `Listing all files failed: ${response.status} ${response.statusText}`
            )
        }

        const data: FileInfo[] = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching all uploaded files:', error)
        throw error
    }
}

// Field names (tagName/tagDescription/userName) mirror the unchanged
// `/contributions/tags` API response; only the concept name is "Topic".
export interface Topic {
    id: string
    tagDescription: string
    tagName: string
    userName: string
}

export interface Question {
    id: string
    tag: string[]
    question: string
    answered: boolean
    answerCount?: number
    isAssigned: boolean
}

type ApiQuestion = {
    id: string
    question: string
    tag?: string | string[]
    isAnswered?: boolean
    answered?: boolean
    answerCount?: number
    isAssigned?: boolean
}

const normaliseAvailableQuestion = (
    q: ApiQuestion,
    defaultAnswered = false
): Question => ({
    id: q.id,
    question: q.question,
    tag: Array.isArray(q.tag) ? q.tag : q.tag ? [q.tag] : [],
    answered: q.isAnswered ?? q.answered ?? defaultAnswered,
    answerCount: q.answerCount ?? 0,
    isAssigned: q.isAssigned ?? false,
})

export async function getTopics(idToken: string | undefined): Promise<Topic[]> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/contributions/tags', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Listing catergories failed: ${response.status} ${response.statusText}`
            )
        }

        const data: Topic[] = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching topics:', error)
        throw error
    }
}

export async function getQuestions(
    idToken: string | undefined,
    categoryId?: string,
    entries?: number,
    conversationId?: string,
    // Ranks this topic's questions first without filtering the rest out.
    preferTag?: string
): Promise<Question[]> {
    try {
        const headers = await getHeaders(idToken)
        let query = ''
        if (categoryId) {
            query = `?tag=${encodeURIComponent(categoryId)}`
        }
        if (entries) {
            query += query ? `&entries=${entries}` : `?entries=${entries}`
        }
        if (conversationId) {
            query += query
                ? `&exclude_conversation_id=${encodeURIComponent(conversationId)}`
                : `?exclude_conversation_id=${encodeURIComponent(conversationId)}`
        }
        if (preferTag) {
            query += query
                ? `&prefer_tag=${encodeURIComponent(preferTag)}`
                : `?prefer_tag=${encodeURIComponent(preferTag)}`
        }
        const response = await fetchWithAuth(
            `/api/v1/contributions/available-questions${query}`,
            {
                method: 'GET',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Listing questions failed: ${response.status} ${response.statusText}`
            )
        }

        const data: ApiQuestion[] = await response.json()
        return data.map((q) => normaliseAvailableQuestion(q))
    } catch (error) {
        console.error('Error fetching categories:', error)
        throw error
    }
}

export async function getAnsweredQuestionsRecent(
    idToken: string | undefined,
    entries?: number
): Promise<Question[]> {
    try {
        const headers = await getHeaders(idToken)
        let query = ''
        if (entries) {
            query = `?entries=${entries}`
        }
        const response = await fetchWithAuth(
            `/api/v1/contributions/answered-questions${query}`,
            {
                method: 'GET',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Listing answered questions failed: ${response.status} ${response.statusText}`
            )
        }

        const data: ApiQuestion[] = await response.json()
        return data.map((q) => normaliseAvailableQuestion(q, true))
    } catch (error) {
        console.error('Error fetching answered questions:', error)
        throw error
    }
}

export async function getSingleQuestion(
    idToken: string | undefined
): Promise<Question[]> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth(
            `/api/v1/contributions/available-questions?entries=1`,
            {
                method: 'GET',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Listing question failed: ${response.status} ${response.statusText}`
            )
        }

        const data: ApiQuestion[] = await response.json()
        return data.map((q) => normaliseAvailableQuestion(q))
    } catch (error) {
        console.error('Error fetching categories:', error)
        throw error
    }
}

export interface Example {
    question: string
}

export async function getExampleQuestions(
    idToken: string | undefined
): Promise<Example[]> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/rag/examples', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Listing examples failed: ${response.status} ${response.statusText}`
            )
        }

        const data: Example[] = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching examples:', error)
        throw error
    }
}

//account settings api calls

export interface AccountSettings {
    acceptedTerms: boolean
    canContact: boolean
    dateAcceptedTerms: string | null
    darkMode: boolean
    showMetrics: boolean
    languagePreference: {
        language: string
        locale: string
    }
    userName: string
}

export async function getAccountSettings(
    idToken: string | undefined
): Promise<AccountSettings> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile/settings', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Listing account settings failed: ${response.status} ${response.statusText}`
            )
        }

        const data: AccountSettings = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching account settings:', error)
        throw error
    }
}

export interface UpdateAccountSettingsRequest {
    canContact?: boolean
    acceptedTerms?: boolean
    darkMode?: boolean
    showMetrics?: boolean
    languagePreference?: {
        language: string
        locale: string
    }
}

export async function updateAccountSettings(
    settings: UpdateAccountSettingsRequest,
    idToken: string | undefined
): Promise<{ message: string }> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile/settings', {
            method: 'PATCH',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(settings),
        })

        if (!response.ok) {
            throw new Error(
                `Updating account settings: ${response.status} ${response.statusText}`
            )
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error updating account settings:', error)
        throw error
    }
}

export interface Profile {
    bio?: string | null
    city?: string | null
    contact?: { [key: string]: any } | null
    country?: string | null
    coverPictureFilename?: string | null
    coverPictureVariants?: { [key: string]: string } | null
    createdAt?: string | null
    firstName?: string
    hasCover?: boolean
    hasPicture?: boolean
    id?: string
    impactStatement?: string | null
    jobTitle?: string
    keywords?: string[]
    lastName?: string
    links?: {
        url: string
        linkType: string
        linkTypeName: string
    }[]
    metrics?: {
        knowledgeWordCount?: number
        nextRank?: string
        questionWordCount?: number
        totalInteractions?: number
        userKnowledge?: number
        userLevel?: number
        userPoints?: number
        userProgress?: number
        userQuestions?: number
        userRank?: string
    }
    organisations?: string[]
    profilePictureFilename?: string
    profilePictureVariants?: { [key: string]: string } | null
    updatedAt?: string
    userName?: string
    userOccupation?: string
}

export async function getProfile(
    idToken: string | undefined
): Promise<Profile> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Listing profile failed: ${response.status} ${response.statusText}`
            )
        }

        const data: Profile = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching profile:', error)
        throw error
    }
}

export async function getProfileCard(
    idToken: string | undefined
): Promise<Profile> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth(
            '/api/v1/profile?groups=profileCard',
            {
                method: 'GET',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Listing profile failed: ${response.status} ${response.statusText}`
            )
        }

        const data: Profile = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching profile:', error)
        throw error
    }
}

export type LinkTypeForm = {
    description: string
    id: string
    name: string
}

export async function getLinkTypes(
    idToken: string | undefined
): Promise<LinkTypeForm[]> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile/link-types', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data: LinkTypeForm[] = await response.json()
        return data
    } catch (error) {
        throw new Error(`Link types error`)
    }
}

export async function updateProfileInfo(
    profile: Profile,
    idToken: string | undefined
): Promise<{ message: string }> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile', {
            method: 'PATCH',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(profile),
        })

        if (!response.ok) {
            throw new Error(
                `Updating profile failed: ${response.status} ${response.statusText}`
            )
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error updating profile:', error)
        throw error
    }
}

export interface PublicProfile {
    bio?: string | null
    city?: string | null
    contact?: string | null
    country?: string | null
    coverPictureVariants?: { [key: string]: string } | null
    firstName?: string
    hasCover?: boolean
    hasPicture?: boolean
    impactStatement?: string | null
    jobTitle?: string | null
    keywords?: string[]
    lastName?: string
    links?: {
        url: string
        linkType: string
        linkTypeName: string
    }[]
    metrics?: {
        knowledgeWordCount?: number
        nextRank?: string
        questionWordCount?: number
        totalInteractions?: number
        userKnowledge?: number
        userLevel?: number
        userPoints?: number
        userProgress?: number
        userQuestions?: number
        userRank?: string
    } | null
    organisations?: string[]
    profilePictureVariants?: { [key: string]: string } | null
}

export type ProfileData = Profile | PublicProfile

export async function getPublicProfile(
    idToken: string | undefined,
    handle: string | undefined
): Promise<PublicProfile> {
    try {
        if (!handle) {
            throw new Error('Handle is required')
        }
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth(
            `/api/v1/profile/handle/${encodeURIComponent(handle)}`,
            {
                method: 'GET',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Listing profile failed: ${response.status} ${response.statusText}`
            )
        }

        const data: PublicProfile = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching profile:', error)
        throw error
    }
}

export interface ListProfile {
    firstName?: string
    lastName?: string
    handle?: string
    bio?: string | null
    city?: string | null
    country?: string | null
    keywords?: string[]
    profilePictureFilename?: string
    userOccupation?: string
}

export interface PaginationMeta {
    currentPage: number
    perPage: number
    totalItems: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
    nextPage: number | null
    prevPage: number | null
}

export interface PaginatedProfiles {
    items: ListProfile[]
    pagination: PaginationMeta
}

export async function getMembersPage(
    page: number,
    perPage: number,
    query: string,
    options?: { signal?: AbortSignal }
): Promise<PaginatedProfiles> {
    try {
        const headers = await getHeaders()
        const params = new URLSearchParams({
            page: String(page),
            per_page: String(perPage),
        })
        if (query) {
            params.set('q', query)
        }
        const response = await fetchWithAuth(
            `/api/v1/profile/list?${params.toString()}`,
            {
                method: 'GET',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
                signal: options?.signal,
            }
        )

        if (!response.ok) {
            throw new Error(
                `Fetching members failed: ${response.status} ${response.statusText}`
            )
        }

        const data: PaginatedProfiles = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching members:', error)
        throw error
    }
}

// Interface for profile updates
export interface ProfileUpdate {
    firstName?: string
    lastName?: string
    userOccupation?: string
    canContact?: boolean
    acceptedTerms?: boolean
}

export async function updateProfile(
    profile: ProfileUpdate,
    idToken: string | undefined
): Promise<{ message: string }> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile', {
            method: 'PATCH',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(profile),
        })

        if (!response.ok) {
            throw new Error(
                `Updating profile failed: ${response.status} ${response.statusText}`
            )
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error updating profile:', error)
        throw error
    }
}

export async function getUserMetrics(idToken: string | undefined) {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile/metrics', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Fetching metrics failed: ${response.status} ${response.statusText}`
            )
        }

        const userMetrics = await response.json()
        return userMetrics
    } catch (error) {
        console.error('Error fetching user metrics:', error)
        throw error
    }
}

export async function getProfileRoles() {
    try {
        const response = await fetchWithAuth('/api/v1/profile/role', {
            method: 'GET',
            headers: {
                Method: 'GET',
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Fetching profile roles failed: ${response.status} ${response.statusText}`
            )
        }

        const profileRoles = await response.json()
        return profileRoles
    } catch (error) {
        console.error('Error fetching profile roles:', error)
        throw error
    }
}

export interface featureFlagType {
    id: string
    description?: string
    enabled: boolean
    conditions?: object
    display_name?: string
}

export interface ConfigSettings {
    data: {
        settings: {
            askTextFieldPlaceholder?: string
            askSubtitle?: string
            contributeTitle?: string
            contributeClauseText?: string
            communityHubName?: string
            communityHubBio?: string
            termsAndConditionsLink?: string
            logoUrl?: string | null
            logoUpdatedAt?: string | null
            brandColors?: string
            askPage?: boolean
            contributePage?: boolean
            profilePage?: boolean
            communityHub?: boolean
            nonFastTranscriptionApiMic?: boolean
            engagePage?: boolean
            capturesPage?: boolean
            knowledgeBio?: string
            knowledgeRules?: string[]
            anonymousPageAccess?: {
                chat?: boolean
                community?: boolean
                contribute?: boolean
                engage?: boolean
                exchange?: boolean
            }
        }
        authSetup?: {
            allowAnonymous: boolean
            requiresAuth: boolean
            strategy: string
        }
        analytics?: {
            posthogKey: string | null
            posthogHost: string | null
            environment: string | null
        }
    }
}

// Get app configuration settings
export async function getAppConfig(
    idToken?: string
): Promise<ConfigSettings | undefined> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/config/appconfig', {
            method: 'GET',
            headers: {
                ...headers,
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        })
        if (!response.ok) {
            throw new Error(
                `Fetching profile roles failed: ${response.status} ${response.statusText}`
            )
        }
        const configurationSettings = await response.json()
        return configurationSettings
    } catch (error) {
        console.error('Error fetching config settings:', error)
        return undefined
    }
}

interface languagePreferenceForm {
    language: string
    locale: string
}

export interface ProfileSettingsForm {
    acceptedTerms: boolean
    roles: string[]
    canContact: boolean
    dateAcceptedTerms: Date | null
    languagePreference: languagePreferenceForm
    hasAccess: boolean
}

export async function getProfileSettings(
    idToken: string | undefined
): Promise<ProfileSettingsForm> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile/settings', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Getting profile settings back failed: ${response.status} ${response.statusText}`
            )
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching profile settings:', error)
        return {} as ProfileSettingsForm
    }
}

export interface CountryOption {
    code: string
    name: string
}

export async function getAvailableCountries(
    idToken: string | undefined
): Promise<CountryOption[]> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile/countries', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Getting countries failed: ${response.status} ${response.statusText}`
            )
        }
        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching countries:', error)
        return [] as CountryOption[]
    }
}

/** One selectable transcription language, as published by the backend. */
export interface SpeechLocale {
    value: string
    label: string
}

/**
 * What the configured speech provider can actually be asked for.
 *
 * The single source for every locale menu (ADR-0016 item 3). This used to be
 * `GET /profile/languages`, a 146-entry table of which the backend accepted 8,
 * filtered here on `fast_transcription_support` — a field the API never sent
 * under that name (it serialises `fastTranscriptionSupport`), so the filter
 * matched nothing and the mic language menu rendered empty.
 */
export interface SpeechCapabilities {
    locales: SpeechLocale[]
    defaultLocale: string
    autoDetect: string
    maxLanguageIdCandidates: number
}

const EMPTY_CAPABILITIES: SpeechCapabilities = {
    locales: [],
    defaultLocale: 'en-GB',
    autoDetect: 'auto',
    maxLanguageIdCandidates: 1,
}

export async function getSpeechCapabilities(
    idToken?: string | undefined
): Promise<SpeechCapabilities> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/speech/capabilities', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Fetching speech capabilities failed: ${response.status} ${response.statusText}`
            )
        }
        return (await response.json()) as SpeechCapabilities
    } catch (error) {
        console.error('Error fetching speech capabilities:', error)
        return EMPTY_CAPABILITIES
    }
}

/** A saved transcription-language preference. */
export interface LanguageOption {
    language: string
    locale: string
}

/**
 * The languages offerable as a *saved* preference.
 *
 * Excludes the auto-detect sentinel: it is a per-recording choice ("identify what
 * I speak this time"), not a stored language, and the settings form persists this
 * straight onto `languagePreference`.
 */
export async function getAllLanguagesAvailable(
    idToken: string | undefined
): Promise<LanguageOption[]> {
    const capabilities = await getSpeechCapabilities(idToken)
    return capabilities.locales
        .filter((locale) => locale.value !== capabilities.autoDetect)
        .map((locale) => ({ locale: locale.value, language: locale.label }))
}

///profile api calls

export interface ProfilePictureProps {
    idToken: string | undefined
}

export async function getProfilePicture(
    idToken: string | undefined,
    size: 'original' | 'medium' | 'small' = 'medium'
): Promise<string | null> {
    try {
        const headers = await getHeaders(idToken)
        const cacheBuster = `&t=${Date.now()}`
        const response = await fetchWithAuth(
            `/api/v1/profile/picture?size=${size}${cacheBuster}`,
            {
                method: 'GET',
                headers: headers,
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Getting profile picture failed: ${response.status} ${response.statusText}`
            )
        }

        const contentType = response.headers.get('content-type') ?? ''
        if (!contentType.startsWith('image/')) return null

        const blob = await response.blob()
        const imageUrl = URL.createObjectURL(blob)

        return imageUrl
    } catch (error) {
        console.error('Error fetching profile picture:', error)
        throw error
    }
}

export async function getCommunityLogo(
    idToken: string | undefined
): Promise<string> {
    const headers = await getHeaders(idToken)
    const cacheBuster = `?t=${Date.now()}`
    const response = await fetchWithAuth(
        `/api/v1/community/logo${cacheBuster}`,
        {
            method: 'GET',
            headers: headers,
            credentials: 'include',
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to fetch logo: ${response.status}`)
    }

    const blob = await response.blob()
    return URL.createObjectURL(blob)
}

export async function getPublicProfilePicture(
    idToken: string | undefined,
    handle: string | undefined,
    size: 'original' | 'medium' | 'small' = 'medium'
): Promise<string | null> {
    try {
        const headers = await getHeaders(idToken)
        const cacheBuster = `&t=${Date.now()}`
        const response = await fetchWithAuth(
            `/api/v1/profile/${handle}/picture?size=${size}${cacheBuster}`,
            {
                method: 'GET',
                headers: headers,
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Getting profile picture failed: ${response.status} ${response.statusText}`
            )
        }

        const contentType = response.headers.get('content-type') ?? ''
        if (!contentType.startsWith('image/')) return null

        const blob = await response.blob()
        const imageUrl = URL.createObjectURL(blob)

        return imageUrl
    } catch (error) {
        console.error('Error fetching profile picture:', error)
        throw error
    }
}

export async function uploadProfilePicture(
    idToken: string | undefined,
    imageBlob: Blob
): Promise<void> {
    try {
        const headers = await getHeaders(idToken)
        const formData = new FormData()
        formData.append('file', imageBlob, 'profile.jpg')

        const response = await fetchWithAuth('/api/v1/profile/picture', {
            method: 'POST',
            headers: {
                ...headers,
            },
            credentials: 'include',
            body: formData,
        })

        if (!response.ok) {
            throw new Error(
                `Upload failed: ${response.status} ${response.statusText}`
            )
        }
    } catch (error) {
        console.error('Error uploading profile picture:', error)
        throw error
    }
}

export async function getCoverPicture(
    idToken: string | undefined,
    size: 'original' | 'medium' | 'small' = 'medium'
): Promise<string | null> {
    try {
        const headers = await getHeaders(idToken)
        const cacheBuster = `&t=${Date.now()}`
        const response = await fetchWithAuth(
            `/api/v1/profile/cover?size=${size}${cacheBuster}`,
            {
                method: 'GET',
                headers: headers,
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Getting cover picture failed: ${response.status} ${response.statusText}`
            )
        }

        const contentType = response.headers.get('content-type') ?? ''
        if (!contentType.startsWith('image/')) return null

        const blob = await response.blob()
        const imageUrl = URL.createObjectURL(blob)

        return imageUrl
    } catch (error) {
        console.error('Error fetching cover picture:', error)
        throw error
    }
}

export async function getPublicCoverPicture(
    idToken: string | undefined,
    handle: string | undefined,
    size: 'original' | 'medium' | 'small' = 'medium'
): Promise<string | null> {
    try {
        const headers = await getHeaders(idToken)
        const cacheBuster = `&t=${Date.now()}`
        const response = await fetchWithAuth(
            `/api/v1/profile/${handle}/cover?size=${size}${cacheBuster}`,
            {
                method: 'GET',
                headers: headers,
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Getting cover picture failed: ${response.status} ${response.statusText}`
            )
        }

        const contentType = response.headers.get('content-type') ?? ''
        if (!contentType.startsWith('image/')) return null

        const blob = await response.blob()
        const imageUrl = URL.createObjectURL(blob)

        return imageUrl
    } catch (error) {
        console.error('Error fetching cover picture:', error)
        throw error
    }
}

export async function uploadCoverPicture(
    idToken: string | undefined,
    imageBlob: Blob
): Promise<void> {
    try {
        const headers = await getHeaders(idToken)
        const formData = new FormData()
        formData.append('file', imageBlob, 'cover.jpg')

        const response = await fetchWithAuth('/api/v1/profile/cover', {
            method: 'POST',
            headers: {
                ...headers,
            },
            credentials: 'include',
            body: formData,
        })

        if (!response.ok) {
            throw new Error(
                `Upload failed: ${response.status} ${response.statusText}`
            )
        }
    } catch (error) {
        console.error('Error uploading cover picture:', error)
        throw error
    }
}

export async function createProfile(
    profile: Profile,
    idToken: string | undefined
): Promise<{ message: string }> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile', {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(profile),
        })

        if (!response.ok) {
            throw new Error(
                `Creating profile failed: ${response.status} ${response.statusText}`
            )
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error creating profile:', error)
        throw error
    }
}

export interface CreateProfileSettingsRequest {
    acceptedTerms: boolean
    canContact: boolean
    languagePreference: {
        language: string
        locale: string
    }
}

export async function createProfileSettings(
    settings: CreateProfileSettingsRequest,
    idToken: string | undefined
): Promise<{ message: string }> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/profile/settings', {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(settings),
        })

        if (!response.ok) {
            throw new Error(
                `Creating profile settings failed: ${response.status} ${response.statusText}`
            )
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error creating profile settings:', error)
        throw error
    }
}

// Community hub metrics

export interface ComparisonMetrics {
    communityAverages: {
        knowledgeWordCount: number
        questionWordCount: number
        totalInteractions: number
        userKnowledge: number
        userQuestions: number
    }
    totalUsers: number
    userMetrics: {
        knowledgeWordCount: number
        questionWordCount: number
        totalInteractions: number
        userKnowledge: number
        userQuestions: number
    }
}

export async function getComparisonMetrics(
    idToken: string | undefined
): Promise<ComparisonMetrics> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth(
            '/api/v1/community/metrics/comparison',
            {
                method: 'GET',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Fetching metrics failed: ${response.status} ${response.statusText}`
            )
        }

        const comparisonMetrics: ComparisonMetrics = await response.json()
        return comparisonMetrics
    } catch (error) {
        console.error('Error fetching user metrics:', error)
        throw error
    }
}

export interface SuggestionWithCount {
    text: string
    count: number
}

export interface SuggestionsResponse {
    suggestions: SuggestionWithCount[]
    keywords: SuggestionWithCount[]
    names: SuggestionWithCount[]
}

export async function suggestProfilesWithCounts(
    prefix: string,
    limit: number = 10
): Promise<SuggestionsResponse> {
    try {
        const headers = await getHeaders()
        const response = await fetchWithAuth(
            `/api/v1/profile/suggest?prefix=${encodeURIComponent(prefix)}&limit=${limit}`,
            {
                method: 'GET',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Profile suggest failed: ${response.status} ${response.statusText}`
            )
        }

        const data = await response.json()

        if (data && data.suggestions && data.keywords && data.names) {
            return {
                suggestions: data.suggestions,
                keywords: data.keywords,
                names: data.names,
            }
        } else {
            console.warn('Unexpected suggest response format:', data)
            return {
                suggestions: [],
                keywords: [],
                names: [],
            }
        }
    } catch (error) {
        console.error('Error getting profile suggestions:', error)
        throw error
    }
}

export interface CommunityMetrics {
    communityEngagement: {
        contributionWordCount: {
            amount: number
            goal: number
            percentage: number
        }
        contributions: {
            amount: number
            goal: number
            percentage: number
        }
        questionsAsked: {
            amount: number
            goal: number
            percentage: number
        }
    }
    comparison: {
        Community: number
        You: number
        statType: string
    }[]
    lendAHand: {
        answeredCount: number
        daysOld: number
        id: string
        lastActivity: string
        lastActivityTs: number
        lowActivityScore: number
        needsAttention: boolean
        question: string
        tag: string[]
        urgency: string
    }[]
    totalMembers: number
    trendingQuestions: {
        answeredCount: number
        engagementRate: number
        id: string
        lastActivity: string
        lastActivityTs: number
        mediumTermActivity: number
        question: string
        recentActivity: number
        shortTermActivity: number
        tag: string | string[]
        trendingScore: number
    }[]
}

export async function getCommunityMetrics(
    idToken: string | undefined
): Promise<CommunityMetrics> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth('/api/v1/community', {
            method: 'GET',
            headers: { ...headers, 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(
                `Fetching metrics failed: ${response.status} ${response.statusText}`
            )
        }

        const communityMetrics: CommunityMetrics = await response.json()
        return communityMetrics
    } catch (error) {
        console.error('Error fetching user metrics:', error)
        throw error
    }
}

export interface MembersCardProfiles {
    bio?: string | null
    city?: string | null
    country?: string | null
    firstName?: string | null
    handle?: string
    keywords?: string[]
    lastName?: string | null
    profilePictureFilename?: string | null
    userOccupation?: string | null
}

// For the array response
export type ProfileList = MembersCardProfiles[]

export async function getMembersCardProfiles(
    idToken: string | undefined,
    limit: number
): Promise<ProfileList> {
    try {
        const headers = await getHeaders(idToken)
        const response = await fetchWithAuth(
            `/api/v1/profile/list?limit=${limit}`,
            {
                method: 'GET',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
            }
        )

        if (!response.ok) {
            throw new Error(
                `Fetching profiles failed: ${response.status} ${response.statusText}`
            )
        }

        const data = await response.json()
        const profiles: ProfileList = data.items ?? data
        return profiles
    } catch (error) {
        console.error('Error fetching profiles for display:', error)
        throw error
    }
}

// Flagged Questions Admin APIs
export async function getFlaggedQuestionsApi(
    idToken: string | undefined,
    limit?: number
): Promise<QuestionsResponse> {
    const headers = await getHeaders(idToken)
    const url = limit
        ? `${BACKEND_URI}/api/v1/tenant-admin/questions/flagged?limit=${limit}`
        : `${BACKEND_URI}/api/v1/tenant-admin/questions/flagged`
    const response = await fetchWithAuth(url, {
        method: 'GET',
        headers: { ...headers },
        credentials: 'include',
    })

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function approveFlaggedQuestionApi(
    request: { questionId: string },
    idToken: string | undefined
): Promise<QuestionResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/tenant-admin/questions/${request.questionId}/approve`,
        {
            method: 'POST',
            headers: { ...headers },
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

export async function rejectFlaggedQuestionApi(
    request: { questionId: string },
    idToken: string | undefined
): Promise<QuestionResponse> {
    const headers = await getHeaders(idToken)
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/tenant-admin/questions/${request.questionId}/reject`,
        {
            method: 'POST',
            headers: { ...headers },
            credentials: 'include',
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

// Anonymous session API functions
export async function createAnonymousSessionApi(
    metadata?: Record<string, any>
): Promise<AnonymousSessionResponse> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/auth/session/create`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ metadata: metadata || {} }),
        }
    )

    const parsedResponse = await response.json().catch((e) => {
        console.error('Failed to parse JSON response:', e)
        return { error: 'Invalid response format' }
    })

    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || 'Unknown error')
    }

    return parsedResponse
}

/**
 * Cold-load auth state. Single source of truth — replaces the prior
 * /auth/me/optional + /auth/tenants + localStorage pattern.
 */
export async function getSession(): Promise<SessionResponse> {
    const response = await fetch(`${BACKEND_URI}/api/v1/auth/session`, {
        method: 'GET',
        credentials: 'include',
    })

    if (response.status === 503) {
        // The server could not check the session (Redis unreachable) and said so
        // without clearing anything. Throw, so a revalidation keeps the state it
        // has instead of signing the user out. A cold load still lands on the
        // login screen, via initialiseAuth's catch.
        throw new Error('Session check temporarily unavailable')
    }

    if (!response.ok) {
        // Backend is unreachable / 5xx — surface as unauthenticated rather
        // than throwing, so the cold-load path renders the login screen.
        return {
            authenticated: false,
            principalType: 'none',
            user: null,
            activeTenant: null,
            tenants: [],
            nextAction: null,
        }
    }

    const session = await response.json()
    return {
        ...session,
        principalType:
            session.principalType ??
            (session.authenticated
                ? 'user'
                : session.activeTenant || session.tenants?.length
                  ? 'anonymous'
                  : 'none'),
    }
}

export async function switchTenant(tenantSlug: string): Promise<void> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/auth/switch-tenant`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tenantSlug }),
            credentials: 'include',
        }
    )

    if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(
            body.detail || `Switch tenant failed: ${response.status}`
        )
    }
}

/**
 * Anonymous equivalent of switchTenant — sets the active tenant on the
 * AnonymousSession row so subsequent reads resolve from the cookie alone.
 * Requires that the session has already visited the target tenant
 * (via QR/join code, invite, or subdomain).
 */
export async function switchTenantAnonymous(tenantSlug: string): Promise<void> {
    const response = await fetch(
        `${BACKEND_URI}/api/v1/auth/switch-tenant-anonymous`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tenantSlug }),
            credentials: 'include',
        }
    )

    if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(
            body.detail || `Switch tenant failed: ${response.status}`
        )
    }
}

export async function initialiseAnonymousSession(): Promise<string | null> {
    try {
        // Always call create endpoint - it will set the cookie properly
        // If a cookie already exists, backend will return existing session
        const sessionResponse = await createAnonymousSessionApi({
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
        })

        return sessionResponse.sessionId
    } catch (error) {
        console.error('Failed to initialise anonymous session:', error)
        return null
    }
}

// Registration API methods
export async function registerWithEmail(
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    acceptedTerms?: boolean,
    canContact?: boolean,
    anonymousId?: string,
    inviteCode?: string
): Promise<RegistrationResponse> {
    // Use plain fetch — not fetchWithAuth — registration is unauthenticated;
    // a 401 here is a real error, not an expired token.
    const response = await fetch(`${BACKEND_URI}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            email,
            password,
            firstName,
            lastName,
            acceptedTerms,
            canContact,
            anonymousId,
            inviteCode,
        }),
    })

    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || error.detail || 'Registration failed')
    }

    return response.json()
}

export async function getInviteDetails(
    inviteCode: string
): Promise<InviteDetailsResponse> {
    // Use plain fetch — invite details are public (no auth required).
    const response = await fetch(
        `${BACKEND_URI}/api/v1/auth/invite/${inviteCode}`,
        {
            method: 'GET',
            credentials: 'include',
        }
    )

    if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        return {
            valid: false,
            error:
                error.error || error.detail || 'Failed to fetch invite details',
        }
    }

    return response.json()
}

export async function loginWithEmail(
    email: string,
    password: string
): Promise<LoginResponse> {
    // Use plain fetch — login is unauthenticated; a 401 (wrong password,
    // unverified account) is a real error, not an expired token.
    //
    // Tenant binding at login time happens via:
    //   - Subdomain (acme.lorefully.com) — resolved server-side from Host header.
    //   - Otherwise, the user logs in tenantless and picks a workspace from
    //     /workspaces (which calls /auth/switch-tenant to re-scope the JWT).
    const response = await fetch(`${BACKEND_URI}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
    })

    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || error.detail || 'Login failed')
    }

    return response.json()
}

export async function previewJoinCode(
    code: string
): Promise<JoinCodePreviewResponse> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/join/${code.trim().toUpperCase()}`,
        { method: 'GET', credentials: 'include' }
    )
    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Invalid join code')
    }
    const data = await response.json()
    return {
        code: data.code,
        tenantId: data.tenantId ?? data.tenant_id,
        tenantSlug: data.tenantSlug ?? data.tenant_slug,
        tenantName: data.tenantName ?? data.tenant_name,
        tenantDescription: data.tenantDescription ?? data.tenant_description,
        label: data.label,
        isValid: data.isValid ?? data.is_valid,
        requiresAuth: data.requiresAuth ?? data.requires_auth ?? true,
        allowsAnonymous: data.allowsAnonymous ?? data.allows_anonymous,
        landingPath: data.landingPath ?? data.landing_path,
    }
}

export async function redeemJoinCode(
    code: string
): Promise<RedeemCodeResponse> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/join/${code.trim().toUpperCase()}`,
        { method: 'POST', credentials: 'include' }
    )
    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to redeem join code')
    }
    const data = await response.json()
    return {
        success: data.success,
        tenantId: data.tenant_id ?? data.tenantId,
        tenantSlug: data.tenant_slug ?? data.tenantSlug,
        tenantName: data.tenant_name ?? data.tenantName,
        landingPath: data.landing_path ?? data.landingPath ?? '/contribute',
        message: data.message,
    }
}

export async function redeemAnonymousJoinCode(
    code: string
): Promise<AnonymousRedeemResponse> {
    const response = await fetch(
        `${BACKEND_URI}/api/v1/join/${code.trim().toUpperCase()}/anonymous`,
        {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        }
    )
    if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.detail || 'Failed to redeem code')
    }
    const data = await response.json()
    return {
        success: data.success,
        tenantId: data.tenant_id ?? data.tenantId ?? null,
        tenantSlug: data.tenant_slug ?? data.tenantSlug ?? null,
        tenantName: data.tenant_name ?? data.tenantName ?? null,
        landingPath: data.landing_path ?? data.landingPath ?? '/contribute',
        requiresAuth: data.requires_auth ?? data.requiresAuth ?? false,
        canonicalHost: data.canonical_host ?? data.canonicalHost ?? null,
        reason: data.reason ?? null,
    }
}

export async function getPendingInvites(): Promise<PendingInvitesResponse> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/auth/invites/pending`,
        { method: 'GET', credentials: 'include' }
    )
    if (!response.ok) {
        throw new Error('Failed to fetch pending invites')
    }
    return response.json()
}

export async function acceptInvite(inviteCode: string): Promise<{
    success: boolean
    tenantId: string
    tenantSlug: string | null
    tenantName: string | null
    message: string
}> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/auth/invites/${inviteCode}/accept`,
        { method: 'POST', credentials: 'include' }
    )
    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to accept invite')
    }
    return response.json()
}

export async function declineInvite(
    inviteCode: string
): Promise<{ success: boolean }> {
    const response = await fetchWithAuth(
        `${BACKEND_URI}/api/v1/auth/invites/${inviteCode}/decline`,
        { method: 'POST', credentials: 'include' }
    )
    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to decline invite')
    }
    return response.json()
}

export async function logout(): Promise<void> {
    // Use plain fetch — not fetchWithAuth — to avoid triggering the
    // auth:refresh-failed → signOut → logout → auth:refresh-failed loop.
    await fetch(`${BACKEND_URI}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
    })
    // Best-effort: swallow errors — state is cleared by signOut() regardless
}

export async function verifyEmail(
    email: string,
    code: string
): Promise<EmailVerificationResponse> {
    // Use plain fetch — email verification is unauthenticated.
    const response = await fetch(`${BACKEND_URI}/api/v1/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, code }),
    })

    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || error.detail || 'Verification failed')
    }

    return response.json()
}

export async function resendVerificationCode(email: string): Promise<{
    message: string
    sendSuccess: boolean
    sendFailureReason?: string | null
}> {
    // Use plain fetch — resend verification is unauthenticated.
    const response = await fetch(
        `${BACKEND_URI}/api/v1/auth/resend-verification`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email }),
        }
    )

    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || error.detail || 'Failed to resend code')
    }

    return response.json()
}

export async function changeEmail(
    currentEmail: string,
    newEmail: string,
    password: string
): Promise<{
    message: string
    sendSuccess: boolean
    sendFailureReason?: string | null
}> {
    // Use plain fetch — change-email carries no session (its caller mistyped
    // their address at registration, so they can neither verify nor log in).
    // The password is what proves ownership; the server also refuses accounts
    // that are already verified.
    const response = await fetch(`${BACKEND_URI}/api/v1/auth/change-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            currentEmail,
            newEmail,
            password,
        }),
    })

    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to change email')
    }

    return response.json()
}

export async function requestManualVerification(
    email: string,
    reason?: string
): Promise<{
    message: string
    status: string
}> {
    // Use plain fetch — manual verification request is unauthenticated.
    const response = await fetch(
        `${BACKEND_URI}/api/v1/auth/request-manual-verification`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                email,
                reason,
            }),
        }
    )

    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to submit verification request')
    }

    return response.json()
}

export async function checkEmailAvailability(
    email: string
): Promise<EmailAvailabilityResponse> {
    // Use plain fetch — email availability check is unauthenticated.
    const response = await fetch(`${BACKEND_URI}/api/v1/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
    })

    if (!response.ok) {
        throw new Error('Failed to check email')
    }

    return response.json()
}

export async function requestPasswordReset(
    email: string
): Promise<{ message: string; sent: boolean }> {
    // Use plain fetch — password reset request is unauthenticated.
    const response = await fetch(`${BACKEND_URI}/api/v1/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
    })

    if (!response.ok) {
        const error = await response.json()
        throw new Error(
            error.error || error.detail || 'Failed to send reset email'
        )
    }

    return response.json()
}

export async function resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string
): Promise<{ message: string }> {
    // Use plain fetch — password reset is unauthenticated.
    const response = await fetch(`${BACKEND_URI}/api/v1/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            resetToken: token,
            newPassword,
            confirmPassword,
        }),
    })

    if (!response.ok) {
        const error = await response.json()
        throw new Error(
            error.error || error.detail || 'Failed to reset password'
        )
    }

    return response.json()
}

/**
 * Change the current (authenticated) user's password. Requires the current
 * password. On success the backend invalidates all *other* sessions and
 * re-mints this session's cookies, so the user stays logged in here.
 */
export async function changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
): Promise<{ message: string }> {
    // Authenticated request — use fetchWithAuth so a stale access token is
    // refreshed transparently (mirrors updateAccountSettings).
    const headers = await getHeaders(undefined)
    const response = await fetchWithAuth('/api/v1/auth/change-password', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            currentPassword,
            newPassword,
            confirmPassword,
        }),
    })

    if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(
            error.error || error.detail || 'Failed to change password'
        )
    }

    return response.json()
}
